import type { TFunction } from 'i18next'
import type {
  GQLBestProvidersForSlice,
  GQLBestProvider as Provider,
  GQLStatsMetric,
} from './generated/best-providers-for'

type GQLStats = NonNullable<GQLBestProvidersForSlice['historicalData']>

const POPULARITY_THRESHOLD = 20
const MIN_BEST_SCORE = 7.0

export type BulletPoint = {
  label: string
  type?: Metric
  primary?: boolean
  providers: Provider[]
}

export type BulletPointsParams = {
  historicalData: GQLStats
  bestCashPayoutProvider: Provider | null
  $t: TFunction
}

export type Metric = 'BESTSCORE' | 'CHEAPEST' | 'FASTEST'

const metricT = {
  BESTSCORE: 'best_providers_for.best_rated',
  CHEAPEST: 'best_providers_for.cheapest',
  FASTEST: 'best_providers_for.fastest',
}
function getMetricLabel(metricType: Metric, $t: TFunction, countProviders: number): string {
  return $t(metricT[metricType], { count: countProviders })
}

const payoutT = {
  Global: '',
  Bank: 'best_providers_for.transfers_to_bank_account',
  Cash: 'best_providers_for.transfers_to_cash_pick_up',
  MobileWallet: 'best_providers_for.transfers_to_mobile',
}
function getPayoutLabel(payout: Payout, $t: TFunction): string {
  return $t(payoutT[payout])
}

/**
 * Skip some bullet points to ensure diversity of providers
 * A bullet point is skipped if the provider is already listed as the first provider in any previous bullet point
 */
function applyDiversityFilter(allBullets: BulletPoint[]): BulletPoint[] {
  const primary = allBullets.filter((bullet) => bullet.primary)
  const secondary = allBullets.filter((bullet) => !bullet.primary)

  const bullets = [...primary]
  const providers = new Set(bullets.map((m) => m.providers[0].slug))

  for (const bullet of secondary) {
    const provider = bullet.providers[0].slug
    if (!providers.has(provider)) {
      bullets.push(bullet)
      providers.add(provider)
    }
  }
  return bullets
}

/**
 * Keep first or popular providers
 */
function filterPopular(popularity: number = POPULARITY_THRESHOLD) {
  return (values: GQLStatsMetric[]) => {
    return values.filter((value, index) => {
      return index === 0 || value.percentage > popularity
    })
  }
}

/**
 * exclude bad scores
 */
function filterBestRated(values: GQLStatsMetric[]) {
  return values.filter((value) => (value.provider.score?.value ?? 0) >= MIN_BEST_SCORE)
}

type Payout = 'Global' | 'Cash' | 'Bank' | 'MobileWallet'

function getLabel(metric: Metric, payout: Payout, $t: TFunction, countProviders: number): string {
  const metricLabel = getMetricLabel(metric, $t, countProviders)
  const payoutLabel = payout !== 'Global' ? `(${getPayoutLabel(payout, $t)})` : ''
  return [metricLabel, payoutLabel].filter(Boolean).join(' ')
}
/**
 * Create a bullet point from a metric
 */
function createBulletPoint(params: {
  metrics: GQLStatsMetric[]
  metric: Metric
  payout: Payout
  primary?: boolean
  filterValues?: (values: GQLStatsMetric[]) => GQLStatsMetric[]
  $t: TFunction
}): BulletPoint {
  const { metrics, metric, payout, primary, $t, filterValues: getValues = (v) => v } = params
  const providers = getValues(metrics).map((value) => value.provider)

  return {
    label: getLabel(metric, payout, $t, providers.length),
    type: metric,
    primary,
    providers,
  }
}

type KeyOfType<T, V> = keyof {
  [P in keyof T as T[P] extends V ? P : never]: unknown
}
type MetricKeys = Array<KeyOfType<GQLStats, GQLStatsMetric | null>>

function getMetrics(historicalData: GQLStats, keys: MetricKeys): GQLStatsMetric[] {
  return keys.map((key) => historicalData[key]).filter(isNotNull)
}

export function createBulletPoints({
  historicalData,
  bestCashPayoutProvider,
  $t,
}: BulletPointsParams): BulletPoint[] {
  const allBulletPoints = [
    createBulletPoint({
      metrics: getMetrics(historicalData, ['bestRated']),
      metric: 'BESTSCORE',
      payout: 'Global',
      primary: true,
      filterValues: filterBestRated,
      $t,
    }),
    createBulletPoint({
      metrics: getMetrics(historicalData, ['cheapest', 'secondCheapest']),
      metric: 'CHEAPEST',
      payout: 'Global',
      primary: true,
      filterValues: filterPopular(),
      $t,
    }),
    createBulletPoint({
      metrics: getMetrics(historicalData, ['fastest']),
      metric: 'FASTEST',
      payout: 'Global',
      primary: true,
      $t,
    }),
    {
      label: $t('best_providers_for.best_cash_pay_out_network', {
        in: historicalData.countryTo.labels.in,
      }),
      providers: bestCashPayoutProvider ? [bestCashPayoutProvider] : [],
    },
    createBulletPoint({
      metrics: getMetrics(historicalData, ['bestRatedCash']),
      metric: 'BESTSCORE',
      payout: 'Cash',
      filterValues: filterBestRated,
      $t,
    }),
    createBulletPoint({
      metrics: getMetrics(historicalData, ['cheapestCash']),
      metric: 'CHEAPEST',
      payout: 'Cash',
      $t,
    }),
    createBulletPoint({
      metrics: getMetrics(historicalData, ['bestRatedBank']),
      metric: 'BESTSCORE',
      payout: 'Bank',
      filterValues: filterBestRated,
      $t,
    }),
    createBulletPoint({
      metrics: getMetrics(historicalData, ['cheapestBank']),
      metric: 'CHEAPEST',
      payout: 'Bank',
      $t,
    }),
    createBulletPoint({
      metrics: getMetrics(historicalData, ['fastestBank']),
      metric: 'FASTEST',
      payout: 'Bank',
      $t,
    }),
    createBulletPoint({
      metrics: getMetrics(historicalData, ['bestRatedWallet']),
      metric: 'BESTSCORE',
      payout: 'MobileWallet',
      filterValues: filterBestRated,
      $t,
    }),
    createBulletPoint({
      metrics: getMetrics(historicalData, ['cheapestWallet']),
      metric: 'CHEAPEST',
      payout: 'MobileWallet',
      $t,
    }),
    createBulletPoint({
      metrics: getMetrics(historicalData, ['fastestWallet']),
      metric: 'FASTEST',
      payout: 'MobileWallet',
      $t,
    }),
  ].filter((item) => item.providers.length)
  return applyDiversityFilter(allBulletPoints)
}
